import { UseFormSetError } from 'react-hook-form'

import { PassengersValues } from 'pages/avia/purchase-form/model'

import { FormPassengersValues } from 'entities/ticket/models/form-passengers'
import { FormTouristValues } from 'entities/tour/models/form-tourist'
import { Account, DOCUMENT, IDocument } from 'entities/user/module'

import {
  BuyTicket,
  PassengerValidateInfo,
  PassengerValidateInfoForm,
} from 'shared/api/BuyTicketModel'
import { TicketInsurance } from 'shared/api/avia/insurance'
import {
  AVAILABLE_ID_PASSPORT,
  TYPE_AGE,
  sourceAggregator,
} from 'shared/constants'
import { capitalizeFirstLetter } from 'shared/lib/capitalizeFirstLetter'
import {
  preparePhoneNumber,
  preparePhoneNumberForRequest,
} from 'shared/lib/prepare-phone-number'
import { TicketItem } from 'shared/models/ticket'

import { getPurchasePassenger } from '../../confirm/lib/preparePurchaseForm'

interface InputType {
  [key: string]: number | undefined
}

interface OutputType {
  [key: string]: { citizenship: string }[]
}

interface Props {
  purchasePassengerData: FormPassengersValues | null
  userData: Account | null
  forMySelf?: boolean
  isAvailableCountries: boolean
  search: InputType
}

interface ValidateUserInfo {
  keyInObject: string
  validateResult: PassengerValidateInfo
  methods?: UseFormSetError<FormPassengersValues>
}

interface PrepareData {
  type: TYPE_AGE
  group: PassengerValidateInfo[]
}

const prepareDataForm = (userData?: IDocument | null) => {
  const data: PassengersValues = {
    id: 'id',
    passport: userData?.document_type as DOCUMENT,
    firstName: userData?.first_name || '',
    lastName: userData?.last_name || '',
    middleName: '',
    gender: String(userData?.gender || ''),
    dateOfBirth: userData?.date_of_birth
      ? new Date(userData?.date_of_birth)
      : '',
    citizenship: userData?.citizenship_obj?.code || '',
    serialDocuments: userData?.document_number || '',
    documentExpirationDate: String(userData?.document_expire || ''),
    doesMiddleName: !!userData?.middle_name?.length,
  }

  return data
}

const generateCitizenshipObject = (input: InputType): OutputType => {
  const output: OutputType = {}

  const citizenship = capitalizeFirstLetter(AVAILABLE_ID_PASSPORT[0])

  for (const [key, value] of Object.entries(input)) {
    output[key] = Array.from({ length: value || 0 }, () => ({
      citizenship: citizenship,
    }))
  }

  return output
}

export const prepareFormPurchase = ({
  purchasePassengerData,
  userData,
  forMySelf,
  isAvailableCountries,
  search,
}: Props) => {
  if (purchasePassengerData) return purchasePassengerData

  const prepare = generateCitizenshipObject(search)

  const preparedPhoneNumber = preparePhoneNumber(userData?.phone_number)

  if (forMySelf) {
    let userInformation: Partial<IDocument> | undefined = isAvailableCountries
      ? userData?.id_card
      : userData?.international_passport

    if (!userInformation && !isAvailableCountries) {
      userInformation = {
        ...userData?.id_card,
        document_number: '',
        document_expire: '',
        document_type: DOCUMENT.PASSPORT,
      }
    }

    prepare[TYPE_AGE.ADULTS][0] = {
      ...prepareDataForm(userInformation as IDocument),
    }

    return {
      email: userData?.email || '',
      phone_number: preparedPhoneNumber,
      ...prepare,
    }
  }

  return {
    email: userData?.email || '',
    phone_number: preparedPhoneNumber,
    ...prepare,
  }
}

const showErrorFormText = ({
  keyInObject,
  validateResult,
  methods,
}: ValidateUserInfo) => {
  if (!validateResult) return

  let firstErrorPath = ''

  Object.keys(validateResult)?.forEach((key) => {
    methods?.(`${keyInObject}.${key}` as keyof FormPassengersValues, {
      type: 'server',
      message: String(validateResult?.[key as keyof Object] || ''),
    })

    if (!firstErrorPath) {
      firstErrorPath = `${keyInObject}.${key}`
    }
  })

  return firstErrorPath
}

export const prepareResponsePurchase = (
  response?: PassengerValidateInfoForm,
  methods?: UseFormSetError<FormPassengersValues | FormTouristValues>,
) => {
  if (!response || !methods) return

  let firstErrorPath = ''

  const processResponse = ({ group, type }: PrepareData) => {
    group?.forEach((form, index) => {
      const errorKey = showErrorFormText({
        methods,
        validateResult: form,
        keyInObject: `${type}.${index}`,
      })

      if (!firstErrorPath && errorKey) {
        firstErrorPath = errorKey
      }
    })
  }

  processResponse({
    type: TYPE_AGE.ADULTS,
    group: response?.adult,
  })

  processResponse({
    type: TYPE_AGE.CHILDREN,
    group: response?.child,
  })

  processResponse({
    type: TYPE_AGE.INFANTS_WITHOUT_SEAT,
    group: response?.infant,
  })

  return firstErrorPath
}

interface DataForBookTicketRequest {
  data?: FormPassengersValues
  selectedTicket?: TicketItem | null
  ticketInsurances?: TicketInsurance[] | null
}

export const prepareDataForRequestBookTicket = ({
  data,
  selectedTicket,
  ticketInsurances,
}: DataForBookTicketRequest) => {
  if (!data || !selectedTicket) {
    throw new Error('Selected ticket is required when data is provided')
  }

  const passengersPurchasePrepared = getPurchasePassenger(data)

  const preparedData: BuyTicket = {
    price: String(selectedTicket?.price?.KGS),
    tid: String(selectedTicket?.tid),
    route_code: selectedTicket!.route_code,
    phone_number: preparePhoneNumberForRequest(data?.phone_number) || '',
    email: data!.email,
    passengers: passengersPurchasePrepared,
    source: selectedTicket?.source as sourceAggregator,
    segments_direction: selectedTicket?.segments_direction,
    uid: selectedTicket!.uid,
    it_ref: selectedTicket!.it_ref,
    insurance_tariffs: ticketInsurances || [],
  }

  return preparedData
}
